import { Box, CircularProgress, Typography } from "@mui/material";
import { useStripe } from "@stripe/react-stripe-js";
import axios from "axios";
import { useEffect, useState } from "react";
import { hostname, parseError } from "../utils";
import { trackPurchaseConversion } from "../utils/gtagUtils";

interface PaymentRedirectHandlerProps {
  getToken: Function;
  eventListener: Function;
  onSuccess: () => void;
  onError: (error: string) => void;
  email?: string;
}

const PaymentRedirectHandler: React.FC<PaymentRedirectHandlerProps> = ({
  getToken,
  eventListener,
  onSuccess,
  onError,
  email,
}) => {
  const stripe = useStripe();
  const [processing, setProcessing] = useState(true);
  const [status, setStatus] = useState("Processing payment...");
  const [hasProcessed, setHasProcessed] = useState(false);

  useEffect(() => {
    const handlePaymentRedirect = async () => {
      // Prevent multiple executions
      if (hasProcessed) {
        return;
      }
      if (!stripe) {
        setStatus("Stripe not loaded...");
        return;
      }

      try {
        // Get URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const paymentIntentClientSecret = urlParams.get(
          "payment_intent_client_secret"
        );
        const redirectStatus = urlParams.get("redirect_status");

        // Get subscription parameters from URL
        const productPlan = urlParams.get("product_plan");
        const billingPeriod = urlParams.get("billing_period");
        const creditLevel = urlParams.get("credit_level");
        const seats = urlParams.get("seats");
        const companies = urlParams.get("companies");
        const creditPackages = urlParams.get("credit_packages");

        if (!paymentIntentClientSecret) {
          console.log("No payment intent client secret found in URL");
          setProcessing(false);
          return;
        }

        setStatus("Confirming payment...");

        // Retrieve the PaymentIntent
        const { paymentIntent, error } = await stripe.retrievePaymentIntent(
          paymentIntentClientSecret
        );

        if (error) {
          console.error("Error retrieving payment intent:", error);
          onError(error.message || "Failed to retrieve payment information");
          setProcessing(false);
          return;
        }

        if (!paymentIntent) {
          onError("Payment information not found");
          setProcessing(false);
          return;
        }

        console.log("Payment Intent Status:", paymentIntent.status);
        console.log("Redirect Status:", redirectStatus);

        // Check if we've already processed this payment intent
        const processedKey = `payment_processed_${paymentIntent.id}`;
        if (sessionStorage.getItem(processedKey)) {
          console.log("Payment already processed, skipping confirmation");
          setStatus("Subscription already activated!");
          onSuccess();
          setProcessing(false);
          return;
        }

        if (paymentIntent.status === "succeeded") {
          setStatus("Payment successful! Activating subscription...");

          // Mark as processed immediately to prevent duplicate calls
          setHasProcessed(true);

          // Confirm the subscription with our backend
          const currentToken = await getToken();

          try {
            // Build payload with conditional fields to ensure they're included
            const confirmPayload: any = {
              paymentIntent: paymentIntent,
              productPlan: productPlan || "entrepreneur",
              billingPeriod: billingPeriod || "monthly",
              seats: parseInt(seats || "1"),
              creditLevel: creditLevel || "standard",
              domain: window.location.hostname,
            };

            // Only add optional fields if they're defined
            if (companies) {
              confirmPayload.companies = parseInt(companies);
            }
            if (creditPackages) {
              confirmPayload.creditPackages = parseInt(creditPackages);
            }

            console.log(
              "Sending confirmation payload (redirect):",
              confirmPayload
            );

            const confirmResponse = await axios.post(
              `${hostname}/subscriptions/confirm`,
              confirmPayload,
              {
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${currentToken}`,
                },
              }
            );

            console.log("Subscription confirmed:", confirmResponse.data);

            // Mark this payment intent as processed
            sessionStorage.setItem(processedKey, "true");

            // Show success message
            eventListener({
              type: "SET_SUCCESS",
              message:
                "Payment successful! Your subscription has been activated.",
            });

            // Google Ads conversion tracking (await to ensure it's sent)
            try {
              const conversionValue = paymentIntent.amount
                ? paymentIntent.amount / 100
                : 1.0;
              await trackPurchaseConversion(
                conversionValue,
                paymentIntent.id,
                email
              );
            } catch (e) {
              console.error("Failed to track conversion:", e);
            }

            // Clean up URL parameters
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete("payment_intent");
            newUrl.searchParams.delete("payment_intent_client_secret");
            newUrl.searchParams.delete("redirect_status");
            newUrl.searchParams.delete("product_plan");
            newUrl.searchParams.delete("billing_period");
            newUrl.searchParams.delete("credit_level");
            newUrl.searchParams.delete("seats");
            newUrl.searchParams.delete("companies");
            newUrl.searchParams.delete("credit_packages");
            window.history.replaceState({}, "", newUrl.toString());

            setStatus("Subscription activated successfully!");
            onSuccess();
          } catch (confirmError) {
            console.error("Error confirming subscription:", confirmError);
            onError(parseError(confirmError));
          }
        } else if (paymentIntent.status === "requires_payment_method") {
          onError(
            "Payment failed. Please try again with a different payment method."
          );
        } else if (paymentIntent.status === "processing") {
          setStatus("Payment is still processing. Please wait...");
          // You might want to poll for status updates here
          setTimeout(() => handlePaymentRedirect(), 3000);
          return;
        } else {
          onError(`Payment ${paymentIntent.status}. Please try again.`);
        }

        setProcessing(false);
      } catch (err) {
        console.error("Error handling payment redirect:", err);
        onError(parseError(err));
        setProcessing(false);
      }
    };

    handlePaymentRedirect();
  }, [stripe]); // Only re-run if stripe instance changes

  if (!processing) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "200px",
        gap: 2,
      }}
    >
      <CircularProgress />
      <Typography variant="body1" color="text.secondary">
        {status}
      </Typography>
    </Box>
  );
};

export default PaymentRedirectHandler;
